import React, { useState, useEffect, useRef } from "react";
import p5 from "p5";
import { scaleLinear, interpolateReds } from "d3";
import { useSelector, useDispatch } from "react-redux";
import { drawScaleLineV3 } from "./ScaleLine";
import { drawPointer } from "../helpers/pointer";
import {
  subregionGeneHeight,
  componentWidth,
  backgroundColour,
  baseline,
  genomeOptions
} from "../constants";
import { setCladeColouring } from "../helpers/setCladeColouring";
import { calculateIntervals } from "../helpers/calculateIntervals";
import { initializeRecord } from "../helpers/helpers";
import { drawPositionPlot } from "../helpers/positionPlot";
import { changePointerPosition, changePointerSelection } from "../redux/reducers/pointerSlice";
import formatActionPayload from "../redux/formatActionPayload";

const Subregion = (props) => {

  const containerRef = useRef();

  // redux data
  const dispatch = useDispatch();
  const state = useSelector(state => state);
  const pointer = state.pointer;
  const slider = state.slider;
  const repeats = state.slider.repeatSelection[props.genomeData.genome];
  const genes = state.slider.geneSelection[props.genomeData.genome];
  const secondaryTrack = state.dashboard.secondaryTrack;
  const sliderStart = state.slider.position[props.genomeData.genome];
  const sliderWidth = state.slider.width[props.genomeData.genome];
  const sliderEnd = sliderStart + sliderWidth;
  const chromosome = state.chromosomes.current[props.genomeData.genome];
  //const chromosomes = Object.keys(props.genomeData.repeatData);
  let max = props.genomeData.maxChromosomePositions[chromosome];

  const Sketch = (p) => {
    let pg, clickedGenes, gdm;

    p.setup = () => {
      p.createCanvas(componentWidth, subregionGeneHeight+baseline*(secondaryTrack.length+1)+5);
      p.noLoop();
    };

    p.draw = () => {
      // view to show the repeats contained in the slider
      p.background(backgroundColour);
      pg = p.createGraphics(componentWidth, subregionGeneHeight*2+baseline+5);
      gdm = p.createGraphics(componentWidth, baseline*Math.max(1, secondaryTrack.length)+5)
      drawSubregion(backgroundColour, subregionGeneHeight);
      drawPointer(p, pointer.position[props.genomeData.genome], ((subregionGeneHeight/2) * (secondaryTrack.length+1) + 5));
    };

    p.mousePressed = () => {
      if (p.mouseY >= 0 && p.mouseY <= baseline + subregionGeneHeight) {
        clickedGenes = [];
        //props.handlePointerMove(p.mouseX, props.genomeData.genome);
        
        let newPointerPos = {...pointer.position, [props.genomeData.genome]: p.mouseX};
        dispatch(changePointerPosition(formatActionPayload(props.genomeData.genome, p.mouseX)));

        // apply same conversion to click to check what's selected by the click
        let convertedX = (p.mouseX / componentWidth) * sliderWidth + sliderStart;
        let threshold = 0.2;
        for (let j = 0; j < repeats.length; j++) {
          let newStart = repeats[j]["calculatedStart"] - threshold;
          let newEnd = repeats[j]["calculatedEnd"] + threshold;
          if (convertedX >= newStart && convertedX <= newEnd) {
            clickedGenes.push(repeats[j]);
          }
        }
        let newPointerSelection = {...pointer.selection, [props.genomeData.genome]: clickedGenes};
        dispatch(changePointerSelection(formatActionPayload(props.genomeData.genome, clickedGenes)));
        //props.handleClickSelection(clickedGenes, props.genomeData.genome);
      }
    };

    const drawSubregion = () => {
      pg.background(backgroundColour);
      pg.strokeWeight(0);

      const startLabel = (sliderStart / componentWidth) * max;
      const endLabel = (sliderEnd / componentWidth) * max;
      
      const actualStart = ((sliderStart - sliderStart) / sliderWidth) * componentWidth;
      const actualEnd = ((sliderEnd - sliderStart) /sliderWidth) * componentWidth;

      const intervals = calculateIntervals(actualStart, actualEnd, 7);
      const labels = calculateIntervals(startLabel, endLabel, 7);

      // TEMP FIX: very ugly, must be dealt with in a nicer way
      if (sliderStart >= componentWidth - sliderWidth) {
        labels[labels.length - 1] = labels[labels.length - 1] + 1;
      }
      drawScaleLineV3(
        pg,
        labels,
        intervals,
        sliderStart,
        sliderWidth,
        subregionGeneHeight + ((subregionGeneHeight/2) * (secondaryTrack.length+1) + 5)
      );

      for (let i = 0; i < repeats.length; i++) {
        // draw repeats
        setCladeColouring(repeats[i].clade, props.clade, pg);
        pg.strokeWeight(0.1);
        let s = (repeats[i]["calculatedStart"] - sliderStart) / sliderWidth;
        let width = (repeats[i]["calculatedEnd"] - repeats[i]["calculatedStart"]) / sliderWidth;
        
        // complete repeats only
        if (repeats[i]["complete"] === "yes" && props.completeFilter.includes("complete")) {
          pg.rect(s * componentWidth, 0, width * componentWidth, subregionGeneHeight);
        }
        // incomplete repeats only
        else if (repeats[i]["complete"] === "no" && props.completeFilter.includes("fragment")){
          pg.rect(s * componentWidth, 0, width * componentWidth, subregionGeneHeight);
        }
        // all repeats
        else if (props.completeFilter.includes("all")) {
          pg.rect(s * componentWidth, 0, width * componentWidth, subregionGeneHeight);
        } 
      }
      p.image(pg, 0, 0);

      var geneColorScale = scaleLinear()
        .domain([props.genomeData.geneDensityMin, props.genomeData.geneDensityMax])
        .range([0, 1]);

      var methylationColorScale = scaleLinear()
        .domain([props.genomeData.methylationDensityMin, props.genomeData.methylationDensityMax])
        .range([0, 1]);

      for (let i=0; i<secondaryTrack.length; i++) {
        let data;
        if (secondaryTrack[i] === "GDM") data = slider.geneSelection[props.genomeData.genome];
        else if (secondaryTrack[i] === "METHYLATION") data = slider.methylationSelection[props.genomeData.genome];
        for (let j=0; j<data.length; j++) {
          let color;
          if (secondaryTrack[i] === "GDM") color = interpolateReds(geneColorScale(data[j]["density"]));
          else if (secondaryTrack[i] === "METHYLATION") color = interpolateReds(methylationColorScale(data[j]["density"]));

          gdm.fill(color)
          let start = (data[j]["calculatedStart"] - sliderStart) / sliderWidth;
          let width = (data[j]["calculatedEnd"] - data[j]["calculatedStart"]) / sliderWidth;
          gdm.rect(start * componentWidth, (subregionGeneHeight/2)*i, width * componentWidth, subregionGeneHeight/2);
        }
      }
      p.image(gdm, 0, subregionGeneHeight + 5)

      // for (let i=0; i<genes.length; i++) {
      //   let color = interpolateReds(colorScale(genes[i]["density"]));
      //   gdm.fill(color)
      //   let gdmStart = (genes[i]["calculatedStart"] - sliderStart) / sliderWidth;
      //   let gdmWidth = (genes[i]["calculatedEnd"] - genes[i]["calculatedStart"]) / sliderWidth;
      //   gdm.rect(gdmStart * componentWidth, 0, gdmWidth * componentWidth, subregionGeneHeight/2);
      // }
      
    };
  };

  useEffect(() => {
    let inst = new p5(Sketch, containerRef.current);
    return () => inst.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
      sliderStart,
      repeats,
      genes,
      props.clade,
      pointer,
      sliderWidth,
      props.genome
    ]
  );

  return (
    <div>
      {repeats.length > 0 &&
        <div ref={containerRef}></div>
      }
    </div>
  );
};
export default Subregion;
